import { getIcon } from './icons.js';

/**
 * Expandable status bar with terminal-style message log.
 *
 * Layout (expanded):
 *   +------------ log area ------------------+
 *   | [10:23:15] Schedule updated     check   |
 *   | [10:23:16] 3 tasks shifted [Undo]      |
 *   +------------ drag handle ---------------+
 *   | [left] status msg  [right] [^] [=]     |  <- status line (always visible)
 *   +----------------------------------------+
 */
export class StatusBar {
    constructor(container, options = {}) {
        this._container = container;
        this._options = options;
        this._expanded = false;
        this._logHeight = 150;
        this._maxHeight = options.maxHeight || 300;
        this._minHeight = options.minHeight || 100;
        this._resizable = options.resizable !== false;
        this._entries = [];

        this._render();
    }

    /** Add a message to the log. */
    log(message, type = 'info', opts = {}) {
        const entry = { message, type, actions: opts.actions || [], time: new Date() };
        this._entries.push(entry);
        this._appendEntry(entry);
        if (!opts.silent) this._updatePreview(message);
    }

    /** Set the persistent status message (shown on the status line). */
    setStatus(msg) {
        this._statusMsg.textContent = msg || '';
    }

    /** Clear all log history. */
    clear() {
        this._entries = [];
        this._logArea.innerHTML = '';
        this._preview.textContent = '';
    }

    /** Expand the log area. */
    expand() {
        if (!this._expanded) this.toggle();
    }

    /** Collapse the log area. */
    collapse() {
        if (this._expanded) this.toggle();
    }

    /** Toggle the log area open/closed. */
    toggle() {
        this._expanded = !this._expanded;
        if (this._expanded) {
            this._logArea.classList.remove('collapsed');
            this._logArea.style.height = this._logHeight + 'px';
            this._dragHandle.style.display = this._resizable ? '' : 'none';
        } else {
            this._logArea.classList.add('collapsed');
            this._logArea.style.height = '';
            this._dragHandle.style.display = 'none';
        }
        this._toggleBtn.innerHTML = this._expanded
            ? getIcon('chevronsDown')
            : getIcon('chevronsUp');
        this._toggleBtn.title = this._expanded ? 'Collapse log' : 'Expand log';
    }

    _render() {
        const wrapper = document.createElement('div');
        wrapper.className = 'nm-status';

        // Log area (above status line)
        this._logArea = document.createElement('div');
        this._logArea.className = 'nm-status-log collapsed';
        wrapper.appendChild(this._logArea);

        // Drag handle
        this._dragHandle = document.createElement('div');
        this._dragHandle.className = 'nm-status-drag';
        this._dragHandle.style.display = 'none';
        wrapper.appendChild(this._dragHandle);
        if (this._resizable) this._setupDrag();

        // Status line (always visible)
        const line = document.createElement('div');
        line.className = 'nm-status-line';

        // Left slot
        const leftSlot = document.createElement('div');
        leftSlot.className = 'nm-status-slot-left';
        if (this._options.slots?.left) leftSlot.appendChild(this._options.slots.left);
        line.appendChild(leftSlot);

        // Status message
        this._statusMsg = document.createElement('span');
        this._statusMsg.className = 'nm-status-msg';
        line.appendChild(this._statusMsg);

        // Preview (latest log when collapsed)
        this._preview = document.createElement('span');
        this._preview.className = 'nm-status-preview';
        line.appendChild(this._preview);

        // Spacer
        const spacer = document.createElement('div');
        spacer.className = 'nm-status-spacer';
        line.appendChild(spacer);

        // Right slot
        const rightSlot = document.createElement('div');
        rightSlot.className = 'nm-status-slot-right';
        if (this._options.slots?.right) rightSlot.appendChild(this._options.slots.right);
        line.appendChild(rightSlot);

        // Expand/collapse toggle
        this._toggleBtn = document.createElement('button');
        this._toggleBtn.className = 'nm-status-toggle-btn';
        this._toggleBtn.innerHTML = getIcon('chevronsUp');
        this._toggleBtn.title = 'Expand log';
        this._toggleBtn.addEventListener('click', () => this.toggle());
        line.appendChild(this._toggleBtn);

        // Toolbar collapse button (optional)
        if (this._options.onToggleToolbar) {
            const menuBtn = document.createElement('button');
            menuBtn.className = 'nm-status-toggle-btn';
            menuBtn.innerHTML = getIcon('menu');
            menuBtn.title = 'Toggle toolbar';
            menuBtn.addEventListener('click', () => this._options.onToggleToolbar());
            line.appendChild(menuBtn);
        }

        wrapper.appendChild(line);
        this._container.appendChild(wrapper);
    }

    _appendEntry(entry) {
        const row = document.createElement('div');
        row.className = `nm-status-entry nm-status-${entry.type}`;

        const time = document.createElement('span');
        time.className = 'nm-status-time';
        const t = entry.time;
        time.textContent = `[${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}:${String(t.getSeconds()).padStart(2, '0')}]`;
        row.appendChild(time);

        const msg = document.createElement('span');
        msg.className = 'nm-status-msg';
        msg.textContent = entry.message;
        row.appendChild(msg);

        if (entry.actions.length > 0) {
            const actions = document.createElement('span');
            actions.className = 'nm-status-actions';
            for (const action of entry.actions) {
                const btn = document.createElement('button');
                btn.className = 'nm-status-action-btn';
                btn.textContent = action.label;
                btn.addEventListener('click', action.onClick);
                actions.appendChild(btn);
            }
            row.appendChild(actions);
        }

        this._logArea.appendChild(row);
        this._logArea.scrollTop = this._logArea.scrollHeight;
    }

    _updatePreview(message) {
        if (!this._expanded) {
            this._preview.textContent = message;
        }
    }

    _setupDrag() {
        let dragging = false;
        let startY = 0;
        let startHeight = 0;

        this._dragHandle.addEventListener('mousedown', (e) => {
            if (!this._expanded) return;
            dragging = true;
            startY = e.clientY;
            startHeight = this._logHeight;
            e.preventDefault();
        });

        document.addEventListener('mousemove', (e) => {
            if (!dragging) return;
            // Dragging up = bigger log area (handle is below log)
            const delta = startY - e.clientY;
            const newHeight = Math.max(this._minHeight, Math.min(this._maxHeight, startHeight + delta));
            this._logHeight = newHeight;
            this._logArea.style.height = newHeight + 'px';
        });

        document.addEventListener('mouseup', () => {
            dragging = false;
        });
    }
}
